<template>
  <div
    class="pagination"
    tabindex="0"
    @focus="focussed = true"
    @blur="focussed = false"
  >
    <EventListener
      v-if="focussed && pageIndex > 1"
      @keydown.prevent.left="emitPageNumber(pageIndex - 1)"
    />
    <EventListener
      v-if="focussed && pageIndex < pageCount"
      @keydown.prevent.right="emitPageNumber(pageIndex + 1)"
    />
    <TablePageButton
      :disabled="pageIndex === 1"
      direction="prev"
      class="pagination__page_reduction_button"
      @click="emitPageNumber(pageIndex - 1)"
    />
    <div
      v-if="!hideNumericButtons"
      class="pagination__numeric_button_container"
    >
      <template v-for="paginationItem in renderTree">
        <button
          v-if="paginationItem !== '...'"
          :key="`${paginationItem} + ${Math.random()}`"
          class="pagination__item pagination__item--button"
          :aria-selected="paginationItem + 1 === pageIndex"
          @click="emitPageNumber(paginationItem + 1)"
        >
          {{ paginationItem + 1 }}
        </button>
        <span
          v-else
          class="pagination__item pagination__item--divider"
          :key="`${paginationItem} + ${Math.random()}`"
          >&mldr;</span
        >
      </template>
    </div>
    <TablePageButton
      :disabled="pageIndex === pageCount"
      direction="next"
      class="pagination__page_increment_button"
      @click="emitPageNumber(pageIndex + 1)"
    />
  </div>
</template>

<script>
import TablePageButton from "../atoms/TablePageButton.vue";
import EventListener from "../atoms/EventListener.vue";

// @vuese
// See in action [here](/pagination-preview)
export default {
  name: "Pagination",
  components: {
    TablePageButton,
    EventListener,
  },
  props: {
    // Amount of pages there are
    pageCount: {
      type: Number,
      required: true,
    },
    // Index of what page the user is on
    pageIndex: {
      type: Number,
      required: true,
    },
    // Amount of items on EACH side of the current page showing as the active range in the pagination.
    activeRangeArea: {
      type: Number,
      default: 2,
    },
    // Flag that forces the hiding of numeric controls.
    hideNumericButtons: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      startRange: [0],
      focussed: false,
    };
  },
  computed: {
    paginationItems() {
      return Array.from(Array(this.pageCount).keys());
    },
    activeRange() {
      const { pageIndex, activeRangeArea, pageCount } = this;
      const endPosition = 1 + this.activeRangeArea * 2;
      if (endPosition > pageCount) return this.paginationItems;
      let startPosition = pageIndex - (activeRangeArea + 1);
      if (startPosition < 0) startPosition = 0;
      if (pageIndex + endPosition > pageCount) startPosition = pageCount - 5;
      const activeRange = [];
      for (let i = 0; i < endPosition; i += 1) {
        activeRange.push(startPosition + i);
      }
      return activeRange;
    },
    endRange() {
      const { paginationItems } = this;
      return [paginationItems[paginationItems.length - 1]];
    },
    showStartRange() {
      const { activeRange, startRange } = this;
      return activeRange.indexOf(startRange[0]) < 0;
    },
    showEndRange() {
      const { activeRange, endRange } = this;
      return activeRange.indexOf(endRange[0]) < 0;
    },
    renderTree() {
      const {
        showStartRange,
        startRange,
        activeRange,
        showEndRange,
        endRange,
      } = this;
      let renderTree = [];
      if (showStartRange) renderTree = [...startRange, "..."];
      renderTree = [...renderTree, ...activeRange];
      if (showEndRange) renderTree = [...renderTree, "...", ...endRange];
      return renderTree;
    },
  },
  methods: {
    emitPageNumber(pageNumber) {
      // Fires when one of the pagination items or pagination buttons is clicked.
      // @arg The argument is a page number.
      this.$emit("pageChange", pageNumber);
      // Avoid pagination jump function
      setTimeout(function () {
        const scrollElement = document.querySelector(".pagination");
        scrollElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 100);
    },
  },
};

// Documentation Prop Exports
const documentationProps = {
  pageCount: 100,
  pageIndex: 33,
  activeRangeArea: 2,
};

export { documentationProps };
</script>

<style scoped lang="scss">
@import "~include-media";
@import "../../style_variables/style_variables.scss";

.pagination {
  display: inline-flex;
}
.pagination__item {
  height: 44px;
  display: flex;
  justify-content: center;
  align-items: center;

  &[aria-selected="true"] {
    font-weight: 700;
    text-decoration: none;
  }
}
.pagination__item--button {
  @extend %body2_style;
  @extend %anchor_style;
  font-variant-numeric: tabular-nums;
  font-size: 0.75rem;
  -webkit-appearance: none;
  border: 0;
  background: transparent;
}
.pagination__numeric_button_container {
  margin: 0 4px;
  display: inline-flex;

  @include media("<tablet") {
    display: none;
  }
}
.pagination__page_reduction_button {
  margin-right: 6px;
}
.pagination__page_increment_button {
  margin-left: 6px;
}
</style>
